import {
  ContractEndActionEnum,
  ContractStatusEnum,
  IContract,
} from '@monetize/types/app';
import { createColumnHelper } from '../../components/Monetize/DataTableNew/appTable';
import { MdInfo } from 'react-icons/md';
import { ContractRenewalIndicator } from '../../components/Contracts/ContractRenewalIndicator';
import { MFlex, MIcon, MTag, MText, MTooltip } from '../../components/Monetize';
import { actionCatalogColumn } from '../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  currencyBodyTemplate,
  dateBodyTemplate,
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  idWithExtraBodyTemplate,
  nameWithIdBodyTemplate,
} from '../../components/Monetize/DataTableNew/tableUtilsNew';
import { CONTRACTS } from '../../constants';
import { getAccountDetailRoute } from '../../constants/routes';
import { IContractWithBillGroup } from '../../hooks/AccountDetail';

const columnHelper = createColumnHelper<IContractWithBillGroup>();

export type ContractListColumnContext =
  ColumnContextBase<IContractWithBillGroup>;

/** tableId: FilterStateKeys.CONTRACT_LIST */
export const CONTRACT_LIST_CATALOG: ColumnCatalog<
  IContractWithBillGroup,
  ContractListColumnContext
> = [
  {
    id: 'id',
    label: 'ID',
    customizable: false,
    build: () =>
      columnHelper.accessor('id', {
        ...getColHeaderWithSize('ID', 200),
        enableSorting: false,
        cell: idWithExtraBodyTemplate({
          children: (data) => {
            const contract = data.row.original as IContract;
            return <ContractRenewalIndicator contract={contract} />;
          },
          extraProps: {
            justifyContent: 'space-between',
          },
        }),
      }),
  },
  {
    id: 'accountName',
    label: 'Account',
    build: () =>
      columnHelper.accessor('accountName', {
        ...getColHeaderWithSize('Account', 220),
        enableSorting: false,
        cell: nameWithIdBodyTemplate<IContractWithBillGroup, string>({
          nameProp: 'accountName',
          idProp: 'accountId',
          idLinkFn: (id) => getAccountDetailRoute(id),
        }),
      }),
  },
  {
    id: 'startDate',
    label: 'Start Date',
    build: () =>
      columnHelper.accessor('startDate', {
        ...getColHeaderWithSize('Start Date', 150),
        ...getColHeaderFilters('DATE'),
        cell: dateBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'endDate',
    label: 'End Date',
    build: () =>
      columnHelper.accessor('endDate', {
        ...getColHeaderWithSize('End Date', 150),
        ...getColHeaderFilters('DATE'),
        cell: dateBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'endAction',
    label: 'Renewal Action',
    build: () =>
      columnHelper.accessor('endAction', {
        ...getColHeaderWithSize('Renewal Action', 180),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            ContractEndActionEnum,
            CONTRACTS.CONTRACT_END_ACTION_UI_DISPLAY,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: CONTRACTS.CONTRACT_END_ACTION_UI_DISPLAY,
        }),
        enableSorting: true,
      }),
  },
  {
    id: 'status',
    label: 'Status',
    build: () =>
      columnHelper.accessor('status', {
        ...getColHeaderWithSize('Status', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            ContractStatusEnum,
            CONTRACTS.CONTRACT_STATUS_DISPLAY,
          ),
        }),
        cell: (cellContext) => {
          const { row } = cellContext;
          const data = row.original as IContractWithBillGroup;
          if (data.renewed) {
            return <MTag variant="purple">Renewed</MTag>;
          } else if (data.status === ContractStatusEnum.PENDING) {
            return (
              <MTooltip
                label={
                  'Your contract is pending because the first invoice of the renewal contract has not yet been generated, or invoice generation is set to manual. Progress invoicing if you want to move the contract to an active status.'
                }
              >
                <MFlex alignItems="center">
                  <MText
                    lineClamp={1}
                    maxW="fit-content"
                    h="18px"
                    mr={1}
                    lineHeight="18px"
                  >
                    Pending
                  </MText>
                  <MIcon as={MdInfo} />
                </MFlex>
              </MTooltip>
            );
          } else {
            return enumDisplayBodyTemplate<
              IContractWithBillGroup,
              ContractStatusEnum
            >({
              displayMap: CONTRACTS.CONTRACT_STATUS_DISPLAY,
            })(cellContext);
          }
        },
        enableSorting: true,
      }),
  },
  {
    id: 'totalValue',
    label: 'Total',
    build: () =>
      columnHelper.accessor('totalValue', {
        ...getColHeaderWithSize('Total', 150),
        ...getColHeaderFilters('NUMBER'),
        cell: currencyBodyTemplate({ currencyProperty: 'currency' }),
        enableSorting: true,
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        ...getColHeaderWithSize('Updated', 170),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  // TODO: BE does not provide this field :wtf:
  // columnHelper.accessor('createDate', {
  //   ...getColHeaderWithSize('Created', 180),
  //   ...getColHeaderFilters('DATE'),
  //   cell: dateTimeBodyTemplate(),
  //   enableSorting: true,
  // }),
  actionCatalogColumn<IContractWithBillGroup>(),
];
